import React from 'react';
import {
  Scale, AlertTriangle, Wrench, Clock, Shield,
  FileText, HelpCircle, Zap, BookOpen, MessageCircle,
} from 'lucide-react';
import { saStyles } from './answer/styles';
import {
  Badge,
  Section,
  DecisionLogPanel,
  SourcesPanel,
  StewardToolkitPanel,
  FollowUpPills,
  HotPillsOtherStewards,
} from './answer/panels';
import {
  DeadlineProtectionBanner,
  InformationRequestsPanel,
  Step1MeetingPrepPanel,
  JointPositionSummaryPanel,
  SixSectionView,
} from './answer/grievanceEnginePanels';
import { GodModeUnderstoodPanel, GodModeClarifyPills } from './answer/GodModePanels';

/* -- Main Component -- */

export default function StructuredAnswerCard({ answer, onFollowUpClick }) {
  if (!answer) return null;
  const citation = answer?.rule?.citation;

  // Exact contract wording — ONLY from exact_contract_wording (no fallback)
  const ecw = answer?.exact_contract_wording;
  const exactQuote = ecw?.quote || null;
  const responseMode = answer?.response_mode || answer?.mode;
  const showExactWording =
    responseMode === 'contract_grounded' &&
    !!exactQuote?.trim();

  /* Clarify-only mode: minimal card */
  if (answer.mode === 'clarify_only' && answer.clarifying_question) {
    const clarQs = answer.intent_clarifying_questions || [];
    return (
      <div className="sa-card sa-card-clarify" data-testid="structured-answer-card">
        <style>{saStyles}</style>
        <DeadlineProtectionBanner
          deadline={answer.deadline_protection}
          onAskFollowUp={onFollowUpClick}
        />
        <SixSectionView sections={answer.response_sections} />
        <InformationRequestsPanel requests={answer.information_requests} />
        <Step1MeetingPrepPanel prep={answer.step1_meeting_prep} />
        <JointPositionSummaryPanel
          jps={answer.joint_position_summary}
          sourceQuestion={answer.user_question}
        />
        <GodModeUnderstoodPanel godMode={answer.god_mode} />
        <div className="sa-clarify-inner">
          <HelpCircle size={22} className="sa-clarify-icon-el" />
          <div style={{ flex: 1 }}>
            <div className="sa-clarify-label">Need more detail</div>
            {clarQs.length >= 1 ? (
              <GodModeClarifyPills questions={clarQs} onPick={onFollowUpClick} />
            ) : (
              <p className="sa-clarify-text">{answer.clarifying_question}</p>
            )}
          </div>
        </div>
        <FollowUpPills follow={answer.follow_up_questions} onPick={onFollowUpClick} />
        <HotPillsOtherStewards pills={answer.hot_pills_other_stewards} onPick={onFollowUpClick} />
        <SourcesPanel sources={answer.sources} />
      </div>
    );
  }

  /* Full answer or general guidance */
  return (
    <div className="sa-card" data-testid="structured-answer-card">
      <style>{saStyles}</style>

      {/* Header: Badge + Source */}
      <div className="sa-header">
        <Badge mode={responseMode} />
        {answer.source_label && (
          <div className="sa-source-label" data-testid="source-label">{answer.source_label}</div>
        )}
      </div>

      {/* God Mode: what Stewie understood (intent + entities). */}
      <GodModeUnderstoodPanel godMode={answer.god_mode} />

      {/* P72: Deadline Protection banner — always FIRST so stewards see it. */}
      <DeadlineProtectionBanner
        deadline={answer.deadline_protection}
        onAskFollowUp={onFollowUpClick}
      />

      {/* P71: Strict 6-section view — Local 15 Grievance Engine spec §16.
          Headlines the answer before all the detailed sections below. */}
      <SixSectionView sections={answer.response_sections} />

      {/* P73: Information Requests to Consider — ranked HIGH/MEDIUM/LOW
          evidence/document requests for the detected grievance type. */}
      <InformationRequestsPanel requests={answer.information_requests} />

      {/* P74: Step 1 Meeting Prep — collapsible Union Position / Company
          Position / Union Rebuttals scaffold. */}
      <Step1MeetingPrepPanel prep={answer.step1_meeting_prep} />

      {/* P75: Joint Position Summary — collapsible 6-section JPS view
          with one-click PDF export. */}
      <JointPositionSummaryPanel
        jps={answer.joint_position_summary}
        sourceQuestion={answer.user_question}
      />

      {/* General guidance banner */}
      {responseMode === 'general_guidance_clarify' && (
        <div className="sa-guidance-banner" data-testid="general-guidance-banner">
          <AlertTriangle size={15} />
          <span>This is general guidance. Your agreement may have specific language that still needs to be confirmed.</span>
        </div>
      )}

      {/* ═══ EXACT CONTRACT WORDING — contract_grounded only ═══ */}
      {showExactWording && (
        <div className="sa-contract-source" data-testid="exact-contract-wording-block">
          <div className="sa-contract-source-head">
            <Scale size={14} />
            <span className="sa-contract-source-title">EXACT CONTRACT WORDING</span>
            <span className="sa-contract-source-badge" data-testid="source-text-badge">SOURCE TEXT — NOT AI-GENERATED</span>
          </div>
          <div className="sa-contract-source-body">
            <blockquote className="sa-contract-quote" data-testid="exact-contract-quote">
              {exactQuote}
            </blockquote>
            {ecw && (
              <div className="sa-contract-cite" data-testid="exact-contract-citation">
                <FileText size={12} />
                <span>{[
                  ecw.source_title,
                  ecw.article && ecw.article !== 'N/A' ? `Article ${ecw.article}` : null,
                  ecw.section && ecw.section !== 'N/A' ? `Section ${ecw.section}` : null,
                  ecw.page && ecw.page !== 'N/A' ? `Page ${ecw.page}` : null,
                ].filter(Boolean).join(' · ')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Key Takeaway */}
      {answer.key_takeaway && (
        <Section icon={Zap} title="KEY TAKEAWAY" accent="green" testId="section-takeaway">
          <div className="sa-takeaway-text">{answer.key_takeaway}</div>
        </Section>
      )}

      {/* Explanation */}
      {answer.explanation && (
        <Section icon={Scale} title="WHAT IT MEANS" accent="cyan" testId="section-explanation">
          <div>{answer.explanation}</div>
        </Section>
      )}

      {/* Kid Explanation */}
      {answer.kid_explanation && (
        <Section icon={BookOpen} title="LIKE YOU'RE 10" accent="sky" testId="section-kid-explanation">
          <div>{answer.kid_explanation}</div>
        </Section>
      )}

      {/* What Went Wrong */}
      {answer.what_went_wrong && (
        <Section icon={AlertTriangle} title="WHAT WENT WRONG" accent="red" testId="section-violation">
          <div>{answer.what_went_wrong}</div>
        </Section>
      )}

      {/* Action Steps */}
      {answer.action_steps?.length > 0 && (
        <Section icon={Wrench} title="WHAT TO DO NOW" accent="green" testId="section-action-steps">
          <ol className="sa-steps">
            {answer.action_steps.map((step, i) => (
              <li key={i} className="sa-step">
                <span className="sa-step-num">{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Timeline */}
      {answer.timeline && (
        <Section icon={Clock} title="TIMELINE" accent="orange" testId="section-timeline">
          <div>{answer.timeline}</div>
        </Section>
      )}

      {/* Rights Protection */}
      {answer.rights_protection && (
        <Section icon={Shield} title="PROTECT YOUR RIGHTS" accent="pink" testId="section-rights">
          <div>{answer.rights_protection}</div>
        </Section>
      )}

      {/* Grievance Language */}
      {answer.grievance_language && (
        <Section icon={FileText} title="GRIEVANCE LANGUAGE" accent="amber" testId="section-grievance">
          <div className="sa-grievance-text">{answer.grievance_language}</div>
        </Section>
      )}

      {/* Follow-up Question (legacy single question from LLM) */}
      {answer.clarifying_question && (
        <Section icon={MessageCircle} title="FOLLOW-UP QUESTION" accent="amber" testId="section-followup">
          <div className="sa-followup-text">{answer.clarifying_question}</div>
        </Section>
      )}

      {/* Intelligent follow-up pills (issue-specific, clickable) */}
      <FollowUpPills follow={answer.follow_up_questions} onPick={onFollowUpClick} />

      {/* "Other stewards are also asking" social-proof strip (hot pills last 7 days) */}
      <HotPillsOtherStewards pills={answer.hot_pills_other_stewards} onPick={onFollowUpClick} />

      {/* Steward Toolkit — after answer sections, before debug panel */}
      <StewardToolkitPanel toolkit={answer.steward_toolkit} />

      {/* P81: Sources Panel — shows which documents AskStewie cited */}
      <SourcesPanel sources={answer.sources} />

      {/* Decision Log Panel */}
      <DecisionLogPanel log={answer.decision_log} />

      {/* User Question footer */}
      <div className="sa-footer">
        <span className="sa-q-label">Q:</span>
        <span className="sa-q-text" data-testid="user-question">{answer.user_question}</span>
      </div>
    </div>
  );
}
